export const AGENT_TEST_RUN_STATUSES = ['pending', 'running', 'completed', 'partial', 'failed', 'aborted'] as const;
export type AgentTestRunStatus = (typeof AGENT_TEST_RUN_STATUSES)[number];

export const AGENT_TEST_RUN_SCORES_STATUSES = [
  'pending',
  'ready',
  'partial',
  'timed_out',
  'failed',
  'skipped',
] as const;
export type AgentTestRunScoresStatus = (typeof AGENT_TEST_RUN_SCORES_STATUSES)[number];

export const AGENT_TEST_RUN_PROMPT_LABELS = ['production', 'latest'] as const;
export type AgentTestRunPromptLabel = (typeof AGENT_TEST_RUN_PROMPT_LABELS)[number];

export const AGENT_EVAL_RUBRIC_NAMES = [
  'usefulness',
  'tone',
  'factual_precision',
  'task_completed',
  'no_hallucination',
  'escalation',
] as const;
export type AgentEvalRubricName = (typeof AGENT_EVAL_RUBRIC_NAMES)[number];

export const RUBRIC_LABELS: Record<AgentEvalRubricName, string> = {
  usefulness: 'Utilidad',
  tone: 'Tono',
  factual_precision: 'Precisión factual',
  task_completed: 'Tarea completada',
  no_hallucination: 'Sin alucinación',
  escalation: 'Escalación',
};

export const SCORES_STATUS_LABELS: Record<AgentTestRunScoresStatus, string> = {
  pending: 'pendientes',
  ready: 'listos',
  partial: 'parciales',
  timed_out: 'timeout',
  failed: 'fallidos',
  skipped: 'omitidos',
};

export const PROMPT_LABEL_LABELS: Record<AgentTestRunPromptLabel, string> = {
  production: 'Producción',
  latest: 'Latest',
};

export const AGENT_EVAL_DEFAULT_MAX_BUDGET_USD = 25;
export const AGENT_EVAL_DEFAULT_MAX_SCENARIOS = 50;
export const RUNNING_POLL_INTERVAL_MS = 10_000;
export const SCORES_POLL_INTERVAL_MS = 60_000;
export const AGENT_CALLS_PER_SCENARIO = 2;
export const JUDGE_CALLS_PER_SCENARIO = 6;

export interface AgentTestRunUsage {
  promptTokens?: number;
  completionTokens?: number;
  totalTokens?: number;
}

export interface AgentTestRunScoreSummary {
  mean?: number;
  stdDev?: number;
  n?: number;
}

export type AgentTestRunScoresSummary = Partial<Record<AgentEvalRubricName, AgentTestRunScoreSummary>>;

export interface AgentTestRun {
  id: number;
  name: string | null;
  projectId: number;
  subProjectId: number | null;
  commonPromptVersionId: number | null;
  mainPromptVersionId: number | null;
  subprojectPromptVersionId: number | null;
  promptLabel: AgentTestRunPromptLabel;
  model: string | null;
  judgeModel: string | null;
  rootRunId: number | null;
  rootName?: string | null;
  familyCount?: number;
  scenarioIds: number[];
  status: AgentTestRunStatus;
  scoresStatus: AgentTestRunScoresStatus;
  langfuseRunId: string | null;
  langfuseDatasetName: string | null;
  langfuseRunUrl: string | null;
  totalUsage: AgentTestRunUsage | null;
  totalCostUsd: string | number | null;
  scoresSummary: AgentTestRunScoresSummary | null;
  triggerType: 'manual' | 'automatic';
  triggeredBy: number | null;
  errorMessage: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

export const AGENT_TEST_RUN_ITEM_STATUSES = ['completed', 'failed', 'skipped'] as const;
export type AgentTestRunItemStatus = (typeof AGENT_TEST_RUN_ITEM_STATUSES)[number];

export interface AgentTestRunItemEvaluation {
  name: string;
  value: number | null;
  comment?: string;
}

export interface RecordedToolCall {
  name: string;
  arguments: string;
  response: string;
  fromScenarioStub: boolean;
}

export interface RunItemChatMessage {
  role: string;
  content?: string | null;
  tool_calls?: { id: string; function?: { name?: string; arguments?: string } }[];
  tool_call_id?: string;
}

export interface AgentTestRunItem {
  id: number;
  agentTestRunId: number;
  agentTestScenarioId: number;
  iterationIndex: number;
  status: AgentTestRunItemStatus;
  usage: AgentTestRunUsage | null;
  costUsd: string | number | null;
  model: string | null;
  output: string | null;
  toolCalls: RecordedToolCall[] | null;
  messages: RunItemChatMessage[] | null;
  evaluations: AgentTestRunItemEvaluation[] | null;
  errorMessage: string | null;
  langfuseItemId: string | null;
  createdAt: string;
}

export interface RunsListParams {
  projectId: number;
  status?: AgentTestRunStatus;
  page: number;
  limit: number;
}

export interface RunsListResponse {
  data: AgentTestRun[];
  pagination: {
    page: number;
    limit: number;
    total: number;
    totalPages: number;
  };
}

export interface PreviousRunSummary {
  id: number;
  name: string | null;
  status: AgentTestRunStatus;
  createdAt: string;
  scoresSummary: AgentTestRunScoresSummary | null;
  totalCostUsd: string | number | null;
  langfuseRunId: string | null;
}

export interface FamilyRunSummary {
  id: number;
  name: string | null;
  status: AgentTestRunStatus;
  createdAt: string;
}

export interface AgentTestRunDetail extends AgentTestRun {
  langfuseUrl: string | null;
  items: AgentTestRunItem[];
  familyRuns: FamilyRunSummary[];
  previousRun: PreviousRunSummary | null;
}

export interface CreateRunBody {
  name?: string;
  projectId: number;
  subProjectId?: number | null;
  promptLabel?: AgentTestRunPromptLabel;
  model?: string | null;
  reRunOfRunId?: number;
  scenarios: number[];
}

export type PreviewRunBody = Pick<CreateRunBody, 'projectId' | 'subProjectId' | 'model' | 'scenarios'>;

export interface ScenarioCostEstimate {
  scenarioId: number;
  avgHistoricalCostUsd: number | null;
  estimatedCostUsd: number | null;
  source: 'history' | 'run_average' | null;
  sampleSize: number;
}

export interface PreviewRunResult {
  scenarioCount: number;
  scenarios: ScenarioCostEstimate[];
  agentCalls: number;
  judgeCalls: number;
  estimatedCostUsd: number | null;
  maxBudgetUsd: number;
  maxScenarios: number;
  withinBudget: boolean;
  withinScenarioLimit: boolean;
  model: string;
  judgeModel: string;
}

export const runDisplayName = (run: Pick<AgentTestRun, 'id' | 'name'>): string =>
  run.name && run.name.trim().length > 0 ? run.name : `agent-test-run-${run.id}`;

export const familyDisplayName = (run: Pick<AgentTestRun, 'id' | 'name' | 'rootRunId' | 'rootName'>): string => {
  if (run.rootRunId === null) return runDisplayName(run);
  return run.rootName && run.rootName.trim().length > 0 ? run.rootName : `agent-test-run-${run.rootRunId}`;
};

export const runPromptVersionLabel = (run: AgentTestRun): string => {
  const versionId = run.subprojectPromptVersionId ?? run.mainPromptVersionId ?? run.commonPromptVersionId;
  const version = versionId != null ? `v${versionId} · ` : '';
  return `${version}${PROMPT_LABEL_LABELS[run.promptLabel]}`;
};

export const runDuration = (run: Pick<AgentTestRun, 'startedAt' | 'finishedAt'>): string => {
  if (!run.startedAt || !run.finishedAt) return '—';
  const seconds = Math.round((new Date(run.finishedAt).getTime() - new Date(run.startedAt).getTime()) / 1000);
  if (seconds < 60) return `${seconds}s`;
  return `${Math.floor(seconds / 60)}m ${seconds % 60}s`;
};

export const formatUsd = (value: string | number | null | undefined): string => {
  if (value === null || value === undefined) return '—';
  const numeric = typeof value === 'string' ? Number(value) : value;
  if (Number.isNaN(numeric)) return '—';
  return `US$ ${numeric.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

export const formatTokens = (usage: AgentTestRunUsage | null): string => {
  if (!usage) return '—';
  const compact = (tokens: number | undefined): string =>
    tokens === undefined ? '?' : tokens >= 1000 ? `${Math.round(tokens / 1000)}k` : String(tokens);
  return `${compact(usage.promptTokens)} in · ${compact(usage.completionTokens)} out`;
};

export const formatScore = (value: number): string =>
  value.toLocaleString('es-CL', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

export type ScoreTone = 'good' | 'warn' | 'bad';

const SCORE_GOOD_MEAN = 0.85;
const SCORE_GOOD_STD_DEV = 0.15;
const SCORE_WARN_MEAN = 0.6;

export const scoreTone = (mean: number, stdDev = 0): ScoreTone => {
  if (mean >= SCORE_GOOD_MEAN && stdDev < SCORE_GOOD_STD_DEV) return 'good';
  if (mean >= SCORE_WARN_MEAN) return 'warn';
  return 'bad';
};

export const SCORE_TONE_CLASSES: Record<ScoreTone, { bg: string; text: string }> = {
  good: { bg: 'bg-success/15', text: 'text-success' },
  warn: { bg: 'bg-warning/15', text: 'text-warning' },
  bad: { bg: 'bg-error/15', text: 'text-error' },
};

export const overallScore = (summary: AgentTestRunScoresSummary | null): number | null => {
  if (!summary) return null;
  const means = Object.values(summary)
    .map((entry) => entry?.mean)
    .filter((mean): mean is number => typeof mean === 'number');
  if (means.length === 0) return null;
  return means.reduce((acc, mean) => acc + mean, 0) / means.length;
};
